import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Sun,
  Moon,
  Cloud,
  CloudSun,
  CloudMoon,
  CloudRain,
  CloudSnow,
  CloudFog,
  CloudLightning,
  MapPin,
  Pencil,
  Search,
} from 'lucide-react';

/* ============================================================
   Weather widget — location-aware, Open-Meteo, time-of-day gradient
   ============================================================ */

const CACHE_KEY = 'emhub_weather_cache_v1';
const LOC_KEY = 'emhub_weather_location_v1'; // { lat, lon, label, tz, manual? }
const GEO_PERMISSION_KEY = 'emhub_weather_geo_tried'; // tombstone so we don't re-prompt
const REFRESH_MS = 15 * 60 * 1000;

/* ---------- WMO code → condition + icon ---------- */

function codeToCondition(code) {
  // returns { family, label, Icon }
  if (code === 0) return { family: 'clear', label: 'Clear sky' };
  if (code === 1) return { family: 'mostlyclear', label: 'Mainly clear' };
  if (code === 2) return { family: 'partlycloudy', label: 'Partly cloudy' };
  if (code === 3) return { family: 'cloudy', label: 'Overcast' };
  if (code === 45 || code === 48) return { family: 'fog', label: 'Fog' };
  if ([51, 53, 55, 56, 57].includes(code))
    return { family: 'drizzle', label: 'Drizzle' };
  if ([61, 63, 65, 66, 67].includes(code))
    return { family: 'rain', label: 'Rain' };
  if ([80, 81, 82].includes(code))
    return { family: 'rain', label: 'Rain showers' };
  if ([71, 73, 75, 77, 85, 86].includes(code))
    return { family: 'snow', label: 'Snow' };
  if ([95, 96, 99].includes(code))
    return { family: 'storm', label: 'Thunderstorm' };
  return { family: 'cloudy', label: 'Unknown' };
}

function pickIcon(family, phase) {
  const isNight = phase === 'night';
  switch (family) {
    case 'clear':
    case 'mostlyclear':
      return isNight ? Moon : Sun;
    case 'partlycloudy':
      return isNight ? CloudMoon : CloudSun;
    case 'cloudy':
      return Cloud;
    case 'fog':
      return CloudFog;
    case 'drizzle':
    case 'rain':
      return CloudRain;
    case 'snow':
      return CloudSnow;
    case 'storm':
      return CloudLightning;
    default:
      return Cloud;
  }
}

/* ---------- Time → phase (dawn / day / golden / night) ---------- */

function computePhase(nowMs, sunriseMs, sunsetMs) {
  if (!sunriseMs || !sunsetMs) {
    // Fallback to fixed bands if API missing.
    const h = new Date(nowMs).getHours();
    if (h < 5 || h >= 20) return 'night';
    if (h < 7) return 'dawn';
    if (h >= 18) return 'golden';
    return 'day';
  }
  const dawnStart = sunriseMs - 30 * 60 * 1000;
  const dawnEnd = sunriseMs + 30 * 60 * 1000;
  const goldenStart = sunsetMs - 30 * 60 * 1000;
  const goldenEnd = sunsetMs + 30 * 60 * 1000;

  if (nowMs >= dawnStart && nowMs < dawnEnd) return 'dawn';
  if (nowMs >= goldenStart && nowMs < goldenEnd) return 'golden';
  if (nowMs >= dawnEnd && nowMs < goldenStart) return 'day';
  return 'night';
}

/* ---------- Background gradient by (phase, weather family) ---------- */

function backgroundFor(phase, family) {
  // Each entry: CSS linear-gradient.
  const map = {
    dawn: {
      default: 'linear-gradient(160deg, #fcb5a0 0%, #f89da6 45%, #b695d9 100%)',
    },
    day: {
      clear:       'linear-gradient(160deg, #6ac1ff 0%, #3f8fe0 60%, #2a6ec2 100%)',
      mostlyclear: 'linear-gradient(160deg, #7ec7ff 0%, #4a95d8 100%)',
      partlycloudy:'linear-gradient(160deg, #8ec6ef 0%, #5f89b5 100%)',
      cloudy:      'linear-gradient(160deg, #9aa4b2 0%, #5b6572 100%)',
      fog:         'linear-gradient(160deg, #c8cdd3 0%, #8d939b 100%)',
      rain:        'linear-gradient(160deg, #5f7386 0%, #37465a 100%)',
      drizzle:     'linear-gradient(160deg, #7b8ea3 0%, #49596c 100%)',
      snow:        'linear-gradient(160deg, #d9e6f1 0%, #9cb3c7 100%)',
      storm:       'linear-gradient(160deg, #3a3b5c 0%, #141527 100%)',
    },
    golden: {
      default:     'linear-gradient(160deg, #ffb37a 0%, #ff7a59 45%, #6a4a8a 100%)',
      storm:       'linear-gradient(160deg, #4a2f4a 0%, #1a1028 100%)',
    },
    night: {
      clear:       'linear-gradient(160deg, #1f2a55 0%, #0c1030 100%)',
      mostlyclear: 'linear-gradient(160deg, #24315c 0%, #10153a 100%)',
      partlycloudy:'linear-gradient(160deg, #2a355f 0%, #161b3e 100%)',
      cloudy:      'linear-gradient(160deg, #2e3344 0%, #14161f 100%)',
      fog:         'linear-gradient(160deg, #3a3f4a 0%, #1a1c22 100%)',
      rain:        'linear-gradient(160deg, #2b3a4d 0%, #0f1724 100%)',
      drizzle:     'linear-gradient(160deg, #2b3a4d 0%, #0f1724 100%)',
      snow:        'linear-gradient(160deg, #39465c 0%, #161c2b 100%)',
      storm:       'linear-gradient(160deg, #2b1c3c 0%, #060512 100%)',
    },
  };
  const phaseMap = map[phase] || map.day;
  return phaseMap[family] || phaseMap.default || phaseMap.clear;
}

/* ---------- Geo resolution ---------- */

async function tryBrowserGeo() {
  if (!navigator.geolocation) throw new Error('no geolocation');
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          label: null, // filled via reverse geocode later
        }),
      (err) => reject(err),
      { timeout: 8000, maximumAge: 10 * 60 * 1000 }
    );
  });
}

async function tryIpGeo() {
  // ipapi.co free, no key. CORS-enabled.
  const res = await fetch('https://ipapi.co/json/');
  if (!res.ok) throw new Error('ipapi failed');
  const j = await res.json();
  if (!j.latitude || !j.longitude) throw new Error('ipapi no coords');
  return {
    lat: j.latitude,
    lon: j.longitude,
    label: [j.city, j.country_name].filter(Boolean).join(', '),
  };
}

async function reverseGeocode(lat, lon) {
  try {
    const url = `https://geocoding-api.open-meteo.com/v1/reverse?latitude=${lat}&longitude=${lon}&count=1&language=en&format=json`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const j = await res.json();
    const r = j?.results?.[0];
    if (!r) return null;
    return [r.name, r.admin1, r.country_code].filter(Boolean).join(', ');
  } catch (_) {
    return null;
  }
}

async function forwardGeocode(query) {
  const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
    query
  )}&count=6&language=en&format=json`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('search failed');
  const j = await res.json();
  return (j.results || []).map((r) => ({
    lat: r.latitude,
    lon: r.longitude,
    label: [r.name, r.admin1, r.country_code].filter(Boolean).join(', '),
    tz: r.timezone,
  }));
}

async function fetchWeather(lat, lon) {
  const url =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${lat}&longitude=${lon}` +
    `&current=temperature_2m,weather_code,wind_speed_10m,relative_humidity_2m,visibility` +
    `&hourly=temperature_2m,weather_code` +
    `&daily=sunrise,sunset` +
    `&forecast_days=2` +
    `&timezone=auto` +
    `&temperature_unit=celsius&wind_speed_unit=kmh`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('open-meteo failed');
  return res.json();
}

/* ---------- Hook: resolve location ---------- */

function loadCachedLocation() {
  try {
    const raw = localStorage.getItem(LOC_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.lat === 'number' && typeof parsed.lon === 'number')
      return parsed;
  } catch (_) {}
  return null;
}

function saveLocation(loc) {
  try {
    localStorage.setItem(LOC_KEY, JSON.stringify(loc));
  } catch (_) {}
}

/* ---------- Responsive size hook ---------- */

function useElementSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') return;
    const el = ref.current;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setSize({
          width: e.contentRect.width,
          height: e.contentRect.height,
        });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

/* ---------- Helpers ---------- */

function formatLocalTime(date, tz) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: tz,
    }).format(date);
  } catch (_) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
}

function formatHour(isoString, tz) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hour12: true,
      timeZone: tz,
    }).format(new Date(isoString));
  } catch (_) {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      hour12: true,
    });
  }
}

function minutesAgo(ts) {
  const diff = Math.round((Date.now() - ts) / 60000);
  if (diff < 1) return 'just now';
  if (diff < 60) return `${diff}m ago`;
  const h = Math.round(diff / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

/* ============================================================
   Manual-location picker modal
   ============================================================ */

const LocationPicker = ({ onClose, onPick }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const r = await forwardGeocode(query.trim());
      setResults(r);
      if (!r.length) setError('No matches.');
    } catch (err) {
      setError('Search failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="weather-picker-backdrop" onClick={onClose}>
      <div
        className="weather-picker"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <div className="weather-picker-head">
          <h4>Set location</h4>
          <button
            type="button"
            className="weather-picker-close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <form className="weather-picker-form" onSubmit={handleSearch}>
          <Search size={14} />
          <input
            autoFocus
            type="text"
            placeholder="City, region or country"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="submit" disabled={loading}>
            {loading ? '…' : 'Search'}
          </button>
        </form>
        {error && <div className="weather-picker-error">{error}</div>}
        {results.length > 0 && (
          <ul className="weather-picker-results">
            {results.map((r, idx) => (
              <li key={`${r.lat}-${r.lon}-${idx}`}>
                <button
                  type="button"
                  onClick={() =>
                    onPick({ ...r, manual: true })
                  }
                >
                  <MapPin size={12} /> {r.label}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="weather-picker-foot">
          <button
            type="button"
            className="weather-picker-auto"
            onClick={() => onPick(null)}
            title="Use automatic location detection"
          >
            Use my current location instead
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   Main component
   ============================================================ */

export default function WeatherWidget() {
  const rootRef = useRef(null);
  const { height } = useElementSize(rootRef);

  const [location, setLocation] = useState(() => loadCachedLocation());
  const [data, setData] = useState(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tick, setTick] = useState(0); // forces re-render every minute

  // Tick every 30s so the local clock + phase update.
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  // Resolve location (manual cached → browser → IP).
  const resolveLocation = useCallback(async () => {
    const cached = loadCachedLocation();
    if (cached?.manual) return cached;

    // Try browser geolocation once per session unless user already granted.
    const alreadyTried = sessionStorage.getItem(GEO_PERMISSION_KEY) === '1';
    if (!alreadyTried) {
      try {
        const g = await tryBrowserGeo();
        const label = await reverseGeocode(g.lat, g.lon);
        const loc = { ...g, label: label || 'Your location' };
        saveLocation(loc);
        sessionStorage.setItem(GEO_PERMISSION_KEY, '1');
        return loc;
      } catch (_) {
        sessionStorage.setItem(GEO_PERMISSION_KEY, '1');
      }
    }
    if (cached) return cached;
    try {
      const ip = await tryIpGeo();
      saveLocation(ip);
      return ip;
    } catch (err) {
      throw new Error('Could not determine location');
    }
  }, []);

  const load = useCallback(async (manualLoc) => {
    setLoading(true);
    setError(null);
    try {
      const loc = manualLoc || (await resolveLocation());
      setLocation(loc);
      const w = await fetchWeather(loc.lat, loc.lon);
      const payload = {
        ...w,
        _location: loc,
        _ts: Date.now(),
      };
      setData(payload);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
      } catch {}
    } catch (err) {
      console.error('Weather load failed:', err);
      setError(err.message || 'Failed to load weather');
    } finally {
      setLoading(false);
    }
  }, [resolveLocation]);

  // Initial + periodic fetch.
  useEffect(() => {
    load();
    const t = setInterval(() => load(), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const handlePick = (picked) => {
    setPickerOpen(false);
    if (!picked) {
      // Clear manual override, go back to auto.
      try {
        localStorage.removeItem(LOC_KEY);
      } catch {}
      sessionStorage.removeItem(GEO_PERMISSION_KEY);
      setLocation(null);
      load();
      return;
    }
    saveLocation(picked);
    setLocation(picked);
    load(picked);
  };

  // ----- Derive view model -----

  const vm = useMemo(() => {
    if (!data?.current) return null;

    const tz = data.timezone || 'UTC';
    const now = new Date();
    const nowMs = now.getTime();

    const todayIdx = 0;
    const sunrise = data?.daily?.sunrise?.[todayIdx]
      ? new Date(data.daily.sunrise[todayIdx]).getTime()
      : null;
    const sunset = data?.daily?.sunset?.[todayIdx]
      ? new Date(data.daily.sunset[todayIdx]).getTime()
      : null;

    const phase = computePhase(nowMs, sunrise, sunset);
    const { family, label } = codeToCondition(data.current.weather_code);
    const Icon = pickIcon(family, phase);

    // Hourly (next 4) — pick entries strictly after now.
    const hours = [];
    if (Array.isArray(data?.hourly?.time)) {
      for (let i = 0; i < data.hourly.time.length && hours.length < 4; i++) {
        const t = new Date(data.hourly.time[i]).getTime();
        if (t <= nowMs) continue;
        hours.push({
          time: data.hourly.time[i],
          temp: Math.round(data.hourly.temperature_2m[i]),
          cond: codeToCondition(data.hourly.weather_code[i]),
        });
      }
    }

    return {
      tz,
      temp: Math.round(data.current.temperature_2m),
      humidity: Math.round(data.current.relative_humidity_2m),
      wind: Math.round(data.current.wind_speed_10m),
      visibilityKm:
        typeof data.current.visibility === 'number'
          ? Math.round(data.current.visibility / 1000)
          : null,
      label,
      family,
      phase,
      Icon,
      hours,
      localTime: formatLocalTime(now, tz),
      location: data._location,
      ts: data._ts,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tick]);

  /* ----- Render states ----- */

  if (!vm) {
    // No data at all: loading or hard failure.
    return (
      <div
        className="weather-widget weather-empty"
        ref={rootRef}
      >
        <div className="weather-title-row">
          <h3>Weather</h3>
          <button
            type="button"
            className="weather-edit-btn"
            onClick={() => setPickerOpen(true)}
            title="Set location"
          >
            <Pencil size={12} />
          </button>
        </div>
        <p className="weather-empty-msg">
          {loading ? 'Loading weather…' : error || 'Weather unavailable'}
        </p>
        {pickerOpen && (
          <LocationPicker
            onClose={() => setPickerOpen(false)}
            onPick={handlePick}
          />
        )}
      </div>
    );
  }

  const background = backgroundFor(vm.phase, vm.family);
  const isDarkTheme =
    vm.phase === 'night' ||
    vm.family === 'storm' ||
    vm.family === 'rain' ||
    (vm.phase === 'day' && vm.family === 'rain');

  const showHourly = height >= 180;
  const showStats = height >= 260;

  const stale = error && data;
  const Icon = vm.Icon;

  return (
    <div
      className={`weather-widget ${isDarkTheme ? 'is-dark' : 'is-light'} phase-${vm.phase} family-${vm.family}`}
      ref={rootRef}
      style={{ background }}
    >
      <button
        type="button"
        className="weather-edit-btn"
        onClick={() => setPickerOpen(true)}
        title="Set location"
      >
        <Pencil size={12} />
      </button>

      <div className="weather-top">
        <div className="weather-temp-block">
          <div className="weather-temp">
            {vm.temp}
            <span className="weather-temp-unit">°</span>
          </div>
          <div className="weather-condition">{vm.label}</div>
        </div>
        <div className="weather-meta">
          <Icon size={44} strokeWidth={1.5} className="weather-icon" />
          <div className="weather-local-time">{vm.localTime}</div>
          {vm.location?.label && (
            <div className="weather-location">
              <MapPin size={10} /> {vm.location.label}
            </div>
          )}
        </div>
      </div>

      {showStats && (
        <div className="weather-stats">
          <div>
            <small>Humidity</small>
            <b>{vm.humidity}%</b>
          </div>
          <div>
            <small>Wind</small>
            <b>{vm.wind} km/h</b>
          </div>
          {vm.visibilityKm !== null && (
            <div>
              <small>Visibility</small>
              <b>{vm.visibilityKm} km</b>
            </div>
          )}
        </div>
      )}

      {showHourly && vm.hours.length > 0 && (
        <div className="weather-hourly">
          {vm.hours.map((h) => {
            const HIcon = pickIcon(h.cond.family, vm.phase);
            return (
              <div className="weather-hourly-item" key={h.time}>
                <small>{formatHour(h.time, vm.tz)}</small>
                <HIcon size={18} strokeWidth={1.5} />
                <b>{h.temp}°</b>
              </div>
            );
          })}
        </div>
      )}

      {stale && (
        <div className="weather-stale">
          Offline — cached {minutesAgo(vm.ts)}
        </div>
      )}

      {pickerOpen && (
        <LocationPicker
          onClose={() => setPickerOpen(false)}
          onPick={handlePick}
        />
      )}
    </div>
  );
}
